import useTranslation from 'next-translate/useTranslation';
import Image from 'next/image';
import React from 'react';
import { Flex, Group, Stack, createStyles, useMantineTheme } from '@mantine/core';
import { IconClockHour5 } from '@tabler/icons-react';

import { AppStoreIcon } from '~/domains/common/components/icons/IconAppStore';
import { GooglePlayIcon } from '~/domains/common/components/icons/IconGooglePlay';
import { Text } from '~/domains/common/components/Text/Text';
import { Title } from '~/domains/common/components/Title/Title';
import { useIsTablet } from '~/domains/common/hooks/mediaQueries';
import { DeepLinkType } from '~/domains/analytics/deepLinks/types';
import { useContestDeepLinkWebToApp } from '~/domains/analytics/deepLinks/useDeepLink';

const useStyles = createStyles((theme) => ({
  circle: {
    background: theme.white,
    padding: theme.spacing.xs,
    borderRadius: /* eslint-disable-line local-rules/use-theme-tokens */ 100,
    height: 64,
    width: 64,
  },
  title: {
    textAlign: 'center',
    maxWidth: 500,
  },
  paragraph: {
    textAlign: 'center',
    color: theme.colors.gray[7],
    maxWidth: 320,
  },
  wrapper: {
    border: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: theme.radius.md,
    background: theme.white,
    padding: theme.other.spacing._8,
  },
  qrCode: {
    display: 'flex',
    '& canvas': {
      height: 84,
      width: 80,
    },
  },
}));

export function GamecenterContestFormatNotSupported({ contestId }: { contestId: string }) {
  const { t } = useTranslation('contest');
  const theme = useMantineTheme();
  const { classes } = useStyles();
  const isTablet = useIsTablet();

  const [appUrl, appLinkQR] = useContestDeepLinkWebToApp({
    contestId,
    deepLinkType: DeepLinkType.UNSUPPORTED_FEATURE,
  });

  return (
    <Stack spacing={theme.other.spacing._32} align="center" justify="center" h="100%">
      <Flex className={classes.circle} align="center" justify="center">
        <IconClockHour5 size={32} strokeWidth={1} />
      </Flex>
      <Stack spacing={theme.spacing.md} align="center">
        <Title className={classes.title} variant="web-headline-medium">
          {t('game.common.soon.title')}
        </Title>

        <Text className={classes.paragraph} variant="web-body-medium">
          {t('game.common.soon.description')}
        </Text>
      </Stack>

      {isTablet || !appLinkQR ? (
        <Stack spacing={theme.spacing.md}>
          <a
            href={appUrl ?? 'https://apps.apple.com/app/id6451190245'}
            target="_blank"
            rel="noreferrer noopener"
          >
            <AppStoreIcon />
          </a>
          <a
            href={appUrl ?? 'https://play.google.com/store/apps/details?id=com.splash.splashsports'}
            target="_blank"
            rel="noreferrer noopener"
          >
            <GooglePlayIcon />
          </a>
        </Stack>
      ) : (
        <>
          <Group className={classes.wrapper}>
            <Image src={appLinkQR} alt="qr code" width={80} height={80} />
            <Image
              src="/app-rating.png"
              alt="rating"
              title="splashsports"
              width={257}
              height={60}
            />
          </Group>
          <Text className={classes.paragraph} variant="web-body-medium">
            {t('game.common.soon.qrcode')}
          </Text>
        </>
      )}
    </Stack>
  );
}
